import { Loader2 } from "lucide-react";
import { Combobox } from "@/components/ui/combobox";
import { useListAllCrmMasters, type CrmMasterSummary } from "@/lib/api/crm/masters";

/** Option label; record names are included so masters with the same name can be told apart. */
function masterOptionLabel(master: CrmMasterSummary): string {
  if (master.records.length === 0) return `${master.long_name} · no records`;
  const names = master.records.slice(0, 3).map((record) => record.long_name);
  const more = master.records.length > names.length ? `, +${master.records.length - names.length} more` : "";
  return `${master.long_name} · ${names.join(", ")}${more}`;
}

/** Picker of every master record (record group). Loads them all; mount it only where it's shown. */
export function MasterCombobox({
  id,
  value,
  onValueChange,
  excludeMasterId,
  placeholder,
  isModal,
  popoverClassName,
  disabled,
}: {
  id: string;
  value: string;
  onValueChange: (masterId: string) => void;
  /** A master not to offer (e.g. the one a record is already in). */
  excludeMasterId?: string;
  placeholder: string;
  isModal?: boolean;
  popoverClassName?: string;
  disabled?: boolean;
}) {
  const mastersResult = useListAllCrmMasters();

  const masters = (mastersResult.data?.pages ?? []).flatMap((page) => page.items);
  const masterOptions = masters
    .filter((master) => master.id !== excludeMasterId)
    .map((master) => ({ value: master.id, label: masterOptionLabel(master) }));
  const isLoadingMasters = mastersResult.isLoading || mastersResult.hasNextPage === true;

  return (
    <>
      <Combobox
        id={id}
        isModal={isModal}
        dwimOnChange
        virtualizeList
        className="w-full"
        popoverClassName={popoverClassName}
        placeholder={placeholder}
        options={masterOptions}
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
      />
      {isLoadingMasters && (
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading group records… {masterOptions.length.toLocaleString()} so far
        </p>
      )}
    </>
  );
}
